import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Stack, Box } from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { EntityModal } from '../shared/EntityModal'
import { FormInput, FormSelect } from '../ui'
import { ConfirmDialog } from '../shared/ConfirmDialog'
import { Lead, LeadSource, UpdateLeadPayload, LEAD_SOURCES, SOURCE_LABELS, INDUSTRY_TYPES } from '../../lib/api/leadsApi'
import { customersApi } from '../../lib/api/customersApi'
import { getCustomerTypes, CustomerType } from '../../lib/api/customerTypesApi'
import { maskPhoneNumber } from '../../utils/phoneUtils'

interface EditLeadDialogProps {
  open: boolean
  lead: Lead
  onClose: () => void
  onSave: (payload: UpdateLeadPayload) => Promise<void>
  isSaving: boolean
}

export function EditLeadDialog({ open, lead, onClose, onSave, isSaving }: EditLeadDialogProps) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    source: '' as LeadSource | '',
    bestTimeToContact: '',
    nextAction: '',
    companyName: '',
    designation: '',
    websiteUrl: '',
    industryType: '',
    customerTypeId: null as number | null,
    fileType: '',
    expectedCloseDate: '',
    expectedAmount: '',
  })
  const [companySearch, setCompanySearch] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  const { data: companySuggestions } = useQuery({
    queryKey: ['company-suggestions', companySearch],
    queryFn: () => customersApi.getAll({ companyNameSearch: companySearch, pageSize: 5 }),
    enabled: companySearch.length > 1,
  })

  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (open) {
      setForm({
        name: lead.name,
        phone: lead.phone,
        email: lead.email ?? '',
        source: (lead.source as LeadSource) ?? '',
        bestTimeToContact: lead.bestTimeToContact ?? '',
        nextAction: lead.nextAction ?? '',
        companyName: lead.companyName ?? '',
        designation: lead.designation ?? '',
        websiteUrl: lead.websiteUrl ?? '',
        industryType: lead.industryType ?? '',
        customerTypeId: lead.customerTypeId ?? null,
        fileType: lead.fileType ?? '',
        expectedCloseDate: lead.expectedCloseDate ? lead.expectedCloseDate.split('T')[0] : '',
        expectedAmount: lead.expectedAmount != null ? String(lead.expectedAmount) : '',
      })
      setErrors({})
      setIsSaveConfirmOpen(false)
    }
  }, [open, lead])

  const set = (k: string, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setErrors((e) => ({ ...e, [k]: '' }))
  }

  const handleSave = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Full name is required'
    // Phone is immutable after creation — no validation needed, field is disabled
    if (!form.source) e.source = 'Source is required'
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = 'Enter a valid email address'
    }
    if (Object.keys(e).length) {
      setErrors(e)
      return
    }
    setIsSaveConfirmOpen(true)
  }

  const handleConfirmSave = async () => {
    setIsSaveConfirmOpen(false)
    // Phone is immutable — intentionally omitted from the payload
    // customerTypeId: 0 signals "clear"; null means "not changed"
    await onSave({
      name: form.name.trim(),
      email: form.email.trim() || null,
      source: form.source as LeadSource,
      bestTimeToContact: form.bestTimeToContact.trim() || null,
      nextAction: form.nextAction.trim() || null,
      companyName: form.companyName.trim() || null,
      designation: form.designation.trim() || null,
      websiteUrl: form.websiteUrl.trim() || null,
      industryType: form.industryType.trim() || null,
      customerTypeId: form.customerTypeId,
      fileType: form.fileType.trim() || null,
      expectedCloseDate: form.expectedCloseDate ? new Date(form.expectedCloseDate).toISOString() : null,
      expectedAmount: form.expectedAmount !== '' ? Number(form.expectedAmount) : null,
    })
  }

  return (
    <>
      <EntityModal
        open={open}
        onClose={onClose}
        onSubmit={handleSave}
        title="Edit Lead"
        submitLabel="Save Changes"
        isSubmitting={isSaving}
        icon={<EditIcon sx={{ fontSize: 18 }} />}
        maxWidth="md"
      >
        <Stack spacing={2}>
          {/* Row 1: Full Name | Phone */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormInput
              label="Full Name *"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
            />
            <FormInput
              label="Phone"
              value={maskPhoneNumber(form.phone)}
              fieldType="phone"
              disabled
              helperText="Phone number cannot be changed"
              fullWidth
            />
          </Box>

          {/* Row 2: Email | Customer Type */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormInput
              label="Email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
            />
            <FormSelect
              label="Customer Type"
              value={form.customerTypeId ?? ''}
              onChange={(v: any) =>
                setForm((f) => ({ ...f, customerTypeId: v?.value ? Number(v.value) : null }))
              }
              options={customerTypes.map((ct) => ({ value: ct.id, label: ct.name }))}
              placeholder="Select Customer Type"
              fullWidth
            />
          </Box>

          {/* Row 3: Designation | Website URL */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormInput
              label="Designation"
              value={form.designation}
              onChange={(e) => set('designation', e.target.value)}
              placeholder="e.g. Manager, Director, CEO"
              fullWidth
            />
            <FormInput
              label="Website URL"
              value={form.websiteUrl}
              onChange={(e) => set('websiteUrl', e.target.value)}
              placeholder="e.g. https://example.com"
              fullWidth
            />
          </Box>

          {/* Row 4: Industry | Source */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormSelect
              label="Industry"
              value={form.industryType}
              onChange={(v: any) => set('industryType', v?.value ?? '')}
              options={INDUSTRY_TYPES.map((i) => ({ value: i, label: i }))}
              fullWidth
            />
            <FormSelect
              label="Source *"
              required
              value={form.source}
              onChange={(v: any) => set('source', v?.value ?? '')}
              options={LEAD_SOURCES.map((s) => ({ value: s, label: SOURCE_LABELS[s] }))}
              error={!!errors.source}
              helperText={errors.source}
              fullWidth
            />
          </Box>

          {/* Row 4.5: File Type | Expected Closing Date */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormInput
              label="File Type"
              value={form.fileType}
              onChange={(e) => set('fileType', e.target.value)}
              placeholder="e.g. PDF, CSV, ZIP"
              fullWidth
            />
            <FormInput
              label="Expected Closing Date"
              type="date"
              value={form.expectedCloseDate}
              onChange={(e) => set('expectedCloseDate', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Box>

          {/* Row 4.6: Expected Order Value */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormInput
              label="Expected Order Value (₹)"
              type="number"
              value={form.expectedAmount}
              onChange={(e) => set('expectedAmount', e.target.value)}
              placeholder="e.g. 50000"
              fullWidth
            />
          </Box>

          {/* Row 5: Description (full width) */}
          <FormInput
            label="Description / Next Action"
            value={form.nextAction}
            onChange={(e) => set('nextAction', e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </EntityModal>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this lead?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}
